/**
 * AssignmentCard Component
 * Displays individual assignment with actions
 */

import { format } from 'date-fns';
import { 
  User, 
  Briefcase, 
  Calendar, 
  MessageSquare, 
  Check, 
  X, 
  MoreVertical,
  Phone,
  Video
} from 'lucide-react';
import { PartnerAssignment, AssignmentStatus } from '@/types/partner-assignments.types';
import { 
  EXPERT_TYPE_LABELS, 
  ASSIGNMENT_STATUS_LABELS 
} from '@/constants/partner-assignments.constants';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface AssignmentCardProps {
  assignment: PartnerAssignment;
  isExpert?: boolean;
  onAccept?: (id: string) => void;
  onDecline?: (id: string) => void;
  onComplete?: (id: string) => void;
  onOpenChat?: (assignment: PartnerAssignment) => void;
  onScheduleCall?: (assignment: PartnerAssignment) => void;
}

const statusColors: Record<AssignmentStatus, string> = {
  pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
  accepted: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  in_progress: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
  completed: 'bg-green-500/10 text-green-500 border-green-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
};

export function AssignmentCard({
  assignment,
  isExpert = false,
  onAccept,
  onDecline,
  onComplete,
  onOpenChat,
  onScheduleCall,
}: AssignmentCardProps) {
  const { language } = useLanguage();
  const lang = language === 'fr' ? 'fr' : 'en';

  const canAccept = isExpert && assignment.status === 'pending';
  const canComplete = isExpert && (assignment.status === 'accepted' || assignment.status === 'in_progress');

  return (
    <Card className={cn(
      'transition-all hover:shadow-md',
      assignment.status === 'cancelled' && 'opacity-60'
    )}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            {/* Expert Type & Status */}
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-foreground">
                {EXPERT_TYPE_LABELS[assignment.expertType][lang]}
              </h3>
              <Badge variant="outline" className={statusColors[assignment.status]}>
                {ASSIGNMENT_STATUS_LABELS[assignment.status][lang]}
              </Badge>
            </div>

            {/* Expert/Client Info */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-2">
              {isExpert ? (
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span>{assignment.clientName || 'Client'}</span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span>{assignment.expertName || 'Expert'}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4" />
                <span>Deal #{assignment.dealId.slice(0, 8)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                <span>{format(assignment.createdAt, 'MMM d, yyyy')}</span>
              </div>
            </div>

            {/* Description */}
            {assignment.description && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {assignment.description}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {canAccept && onAccept && (
              <Button 
                size="sm" 
                onClick={() => onAccept(assignment.id)}
                className="gap-1"
              >
                <Check className="w-4 h-4" />
                {lang === 'fr' ? 'Accepter' : 'Accept'}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onOpenChat && (
                  <DropdownMenuItem onClick={() => onOpenChat(assignment)}>
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {lang === 'fr' ? 'Ouvrir le chat' : 'Open Chat'}
                  </DropdownMenuItem>
                )}
                {onScheduleCall && (
                  <DropdownMenuItem onClick={() => onScheduleCall(assignment)}>
                    <Video className="w-4 h-4 mr-2" />
                    {lang === 'fr' ? 'Planifier un appel' : 'Schedule Call'}
                  </DropdownMenuItem>
                )}
                {canComplete && onComplete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onComplete(assignment.id)}>
                      <Check className="w-4 h-4 mr-2 text-green-500" />
                      {lang === 'fr' ? 'Marquer terminé' : 'Mark Complete'}
                    </DropdownMenuItem>
                  </>
                )}
                {canAccept && onDecline && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => onDecline(assignment.id)}
                      className="text-destructive"
                    >
                      <X className="w-4 h-4 mr-2" />
                      {lang === 'fr' ? 'Refuser' : 'Decline'}
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
